import useSWR, { type SWRConfiguration, type SWRResponse } from 'swr';
import type { CryptoDetail } from '@/type/crypto';
import { useLanguage } from '@/lib/i18n/LanguageContext';

export type CryptoDetailData = 'kline' | 'question' | 'summary' | 'detail';
type PartialCryptoDetail = Partial<CryptoDetail>;
type CryptoDetailDataSelection = [CryptoDetailData, ...CryptoDetailData[]];

interface UseCryptoDetailBaseOptions {
  datas?: CryptoDetailData[];
  startTime?: number;
  endTime?: number;
  fidelityHour?: 1 | 4 | 24;
}

interface UseCryptoDetailAllOptions extends UseCryptoDetailBaseOptions {
  datas?: undefined;
  swr?: SWRConfiguration<CryptoDetail, Error>;
}

interface UseCryptoDetailPartialOptions extends UseCryptoDetailBaseOptions {
  datas: CryptoDetailDataSelection;
  swr?: SWRConfiguration<PartialCryptoDetail, Error>;
}

type UseCryptoDetailOptions = UseCryptoDetailAllOptions | UseCryptoDetailPartialOptions;

const DATA_ORDER: CryptoDetailData[] = ['detail', 'kline', 'question', 'summary'];

function buildCryptoDetailKey(symbol: string, language: string, options: UseCryptoDetailBaseOptions = {}) {
  const normalizedSymbol = symbol.trim().toLowerCase();
  if (!normalizedSymbol) return null;

  const searchParams = new URLSearchParams();
  const requestedDatas = new Set(options.datas);
  const uniqueDatas = DATA_ORDER.filter((data) => requestedDatas.has(data));

  if (uniqueDatas.length > 0) {
    searchParams.set('datas', uniqueDatas.join(','));
  }
  if (options.startTime != null) {
    searchParams.set('start_time', String(options.startTime));
  }
  if (options.endTime != null) {
    searchParams.set('end_time', String(options.endTime));
  }
  if (options.fidelityHour != null) {
    searchParams.set('fidelity_hour', String(options.fidelityHour));
  }
  // Included in the key (not just the Accept-Language header) so switching
  // the in-app language creates a distinct SWR cache entry — otherwise a
  // language switch would keep serving the previously-fetched summary /
  // recommended_questions from the old language until an unrelated
  // revalidation happened to fire.
  searchParams.set('language', language);

  const query = searchParams.toString();
  return `api:/market/crypto/${normalizedSymbol}/detail${query ? `?${query}` : ''}`;
}

function hasSelectedDatas(
  options?: UseCryptoDetailOptions,
): options is UseCryptoDetailPartialOptions {
  return Boolean(options?.datas?.length);
}

export function useCryptoDetail(
  symbol: string,
  options?: UseCryptoDetailAllOptions,
): SWRResponse<CryptoDetail>;
export function useCryptoDetail(
  symbol: string,
  options: UseCryptoDetailPartialOptions,
): SWRResponse<PartialCryptoDetail>;
export function useCryptoDetail(symbol: string, options?: UseCryptoDetailOptions) {
  const { currentLanguage } = useLanguage();
  const key = buildCryptoDetailKey(symbol, currentLanguage.code, options);

  if (hasSelectedDatas(options)) {
    return useSWR<PartialCryptoDetail>(key, {
      revalidateOnFocus: false,
      ...options.swr,
    });
  }

  return useSWR<CryptoDetail>(key, options?.swr);
}
